import React from "react";

import { ExperimentResearchForm } from "./ExperimentResearchForm";
import { useExperimentEditor } from "../../hooks/useExperimentEditor";
import { formatExperimentStatusLabel } from "../../utils/experimentStatus";
import {
  getResearchObjectiveError,
  validateResearchStep,
} from "../../validations/researchValidation";

import type { ExperimentFormData } from "../../types/experiment.types";

type ResearchFieldStatus = "complete" | "incomplete";

interface ExperimentResearchSectionProps {
  experimentData: ExperimentFormData;
  setExperimentData: React.Dispatch<React.SetStateAction<ExperimentFormData>>;
  onFieldHelp?: (fieldType: string) => void;
}

export const ExperimentResearchSection: React.FC<ExperimentResearchSectionProps> = ({
  experimentData,
  setExperimentData,
  onFieldHelp,
}) => {
  const { experimentStatus, isExperimentViewOnly } = useExperimentEditor();
  const researchValidation = React.useMemo(
    () =>
      validateResearchStep({
        researchObjective: experimentData.researchObjective,
        primaryHypothesis: experimentData.primaryHypothesis,
      }),
    [experimentData.primaryHypothesis, experimentData.researchObjective],
  );

  const getFieldStatus = (
    value: string | string[] | undefined | null,
  ): ResearchFieldStatus => {
    if (!value || (typeof value === "string" && !value.trim())) {
      return "incomplete";
    }
    return "complete";
  };

  const validateField = React.useCallback(
    (fieldName: string, value: string) => {
      if (fieldName !== "researchObjective") {
        return true;
      }

      return getResearchObjectiveError(value) === null;
    },
    [],
  );

  React.useEffect(() => {
    window.validateCurrentStep = () => ({
      isValid: researchValidation.isValid,
      missingFields: researchValidation.missingFields,
    });

    return () => {
      window.validateCurrentStep = undefined;
    };
  }, [researchValidation]);

  return (
    <div className="flex flex-col px-0 py-4 sm:py-6 text-white">
      <div className="mx-auto flex w-full max-w-7xl flex-1">
        <div className="w-full">
          <div className="mb-8">
            <div>
              <h2 className="mb-2 flex items-center text-xl font-semibold text-white">
                <div className="mr-3 h-6 w-2 rounded-full bg-amber-400" />
                Research Foundation
              </h2>
              <p className="text-sm text-white/60">
                Define what you&apos;re trying to learn and how you&apos;ll
                know if you&apos;ve succeeded
              </p>
            </div>
          </div>

          {isExperimentViewOnly ? (
            <div className="mb-6 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              This experiment is{" "}
              {formatExperimentStatusLabel(experimentStatus).toLowerCase()} and
              can only be viewed.
            </div>
          ) : null}

          <div className={isExperimentViewOnly ? "pointer-events-none opacity-70" : ""}>
            <ExperimentResearchForm
              experimentData={experimentData}
              setExperimentData={setExperimentData}
              errors={researchValidation.errors}
              validateField={validateField}
              getFieldStatus={getFieldStatus}
              onFieldHelp={onFieldHelp}
              hideHeader
            />
          </div>
        </div>
      </div>
    </div>
  );
};
